import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authFetch } from '../../context/AuthContext';

interface Product {
  id: number; code: string; name: string; category: string;
  price: number; description: string; detail_content: string;
  image_url: string | null; gallery: string[];
  features: string[]; specs: Record<string, string>;
  stock: number; sold_out: number;
}

/* ── 탭 ── */
type Tab = 'basic' | 'detail' | 'gallery' | 'preview';

const TabBtn = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button onClick={onClick}
    className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition ${
      active ? 'border-amber-500 text-amber-600' : 'border-transparent text-stone-500 hover:text-stone-700'
    }`}>
    {children}
  </button>
);

export default function ProductDetailEditor() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product,  setProduct]  = useState<Product | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState<Tab>('basic');
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState('');
  const [errMsg,   setErrMsg]   = useState('');

  /* 편집 상태 */
  const [description,   setDescription]   = useState('');
  const [detailContent, setDetailContent] = useState('');
  const [features,      setFeatures]      = useState<string[]>(['']);
  const [specs,         setSpecs]         = useState<{ key: string; value: string }[]>([{ key: '', value: '' }]);

  /* 갤러리 업로드 */
  const [uploading, setUploading] = useState(false);
  const galleryRef = useRef<HTMLInputElement>(null);

  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  /* ── 로드 ── */
  const loadProduct = async () => {
    if (!id) return;
    const res = await authFetch(`/api/products/${id}`);
    if (!res.ok) { navigate('/admin/catalog'); return; }
    const data = await res.json();

    // JSON 파싱
    let gallery:  string[]                    = [];
    let feats:    string[]                    = [];
    let specData: Record<string, string>      = {};
    try { gallery  = JSON.parse(data.gallery  ?? '[]') || []; } catch {}
    try { feats    = JSON.parse(data.features ?? '[]') || []; } catch {}
    try { specData = JSON.parse(data.specs    ?? '{}') || {}; } catch {}

    setProduct({ ...data, gallery, features: feats, specs: specData });
    setDescription(data.description ?? '');
    setDetailContent(data.detail_content ?? '');
    setFeatures(feats.length ? feats : ['']);
    setSpecs(Object.entries(specData).length
      ? Object.entries(specData).map(([key, value]) => ({ key, value: value as string }))
      : [{ key: '', value: '' }]);
    setLoading(false);
  };

  useEffect(() => { loadProduct(); }, [id]);

  /* ── 기본 정보 + 특징 + 스펙 저장 ── */
  const handleSave = async () => {
    setSaving(true); setErrMsg('');
    const validFeatures = features.filter(f => f.trim());
    const validSpecs    = Object.fromEntries(specs.filter(s => s.key.trim()).map(s => [s.key.trim(), s.value.trim()]));

    const res  = await authFetch(`/api/products/${id}/detail`, {
      method: 'PATCH',
      body: JSON.stringify({
        description, detail_content: detailContent,
        features: validFeatures, specs: validSpecs,
      }),
    });
    setSaving(false);
    if (!res.ok) { const d = await res.json(); setErrMsg(d.error ?? '저장 실패'); return; }
    await loadProduct();
    flash('저장되었습니다.');
  };

  /* ── 갤러리 업로드 ── */
  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd    = new FormData();
      fd.append('image', file);
      const token = localStorage.getItem('cobrand_token');
      await fetch(`/api/products/${id}/gallery`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}` },
        body: fd,
      });
    }
    setUploading(false);
    await loadProduct();
    flash(`${files.length}장 업로드 완료`);
    if (galleryRef.current) galleryRef.current.value = '';
  };

  /* ── 갤러리 이미지 삭제 ── */
  const deleteGalleryImage = async (url: string) => {
    await authFetch(`/api/products/${id}/gallery`, {
      method: 'DELETE',
      body: JSON.stringify({ url }),
    });
    await loadProduct();
    flash('이미지 삭제됨');
  };

  /* ── 특징 목록 헬퍼 ── */
  const setFeature = (i: number, v: string) => setFeatures(prev => prev.map((f, idx) => idx === i ? v : f));
  const addFeature = () => setFeatures(prev => [...prev, '']);
  const removeFeature = (i: number) => setFeatures(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : ['']);

  /* ── 스펙 헬퍼 ── */
  const setSpec = (i: number, field: 'key' | 'value', v: string) =>
    setSpecs(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: v } : s));
  const addSpec    = () => setSpecs(prev => [...prev, { key: '', value: '' }]);
  const removeSpec = (i: number) => setSpecs(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : [{ key: '', value: '' }]);

  if (loading) return <div className="flex items-center justify-center h-full"><p className="text-stone-400 animate-pulse">로드 중...</p></div>;
  if (!product) return null;

  return (
    <div className="h-screen flex flex-col">

      {/* ── 상단 헤더 ── */}
      <div className="bg-white border-b border-stone-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/admin/catalog" className="text-stone-400 hover:text-stone-700 transition">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            </svg>
          </Link>
          <div>
            <h1 className="font-bold text-stone-900 text-sm">{product.name}</h1>
            <p className="text-xs text-stone-400">{product.code} · {product.category}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {toast && <span className="text-green-600 text-sm font-medium">{toast}</span>}
          {errMsg && <span className="text-red-500 text-sm">{errMsg}</span>}
          <a href={`/product/${id}`} target="_blank" rel="noreferrer"
            className="text-xs border border-stone-200 hover:border-amber-400 text-stone-500 hover:text-amber-600 px-3 py-1.5 rounded-lg transition">
            미리보기 ↗
          </a>
          <button onClick={handleSave} disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold px-5 py-2 rounded-lg text-sm transition">
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      {/* ── 탭 ── */}
      <div className="bg-white border-b border-stone-100 px-6 flex gap-0 flex-shrink-0">
        <TabBtn active={tab === 'basic'}   onClick={() => setTab('basic')}>   기본 정보</TabBtn>
        <TabBtn active={tab === 'detail'}  onClick={() => setTab('detail')}>  상세 설명</TabBtn>
        <TabBtn active={tab === 'gallery'} onClick={() => setTab('gallery')}> 갤러리</TabBtn>
        <TabBtn active={tab === 'preview'} onClick={() => setTab('preview')}> 미리보기</TabBtn>
      </div>

      {/* ── 탭 콘텐츠 ── */}
      <div className="flex-1 overflow-auto bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 py-8">

          {/* ── 기본 정보 탭 ── */}
          {tab === 'basic' && (
            <div className="space-y-8">

              {/* 짧은 설명 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-amber-500 rounded-full inline-block"/>
                  짧은 설명
                </h2>
                <p className="text-xs text-stone-400 mb-2">제품 목록에 표시되는 한 줄 설명</p>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  placeholder="예: 고급 천연 소가죽으로 제작된 클래식한 토트백" />
              </div>

              {/* 제품 특징 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-amber-500 rounded-full inline-block"/>
                  제품 특징
                </h2>
                <p className="text-xs text-stone-400 mb-3">• 불릿 목록으로 표시됩니다</p>
                <div className="space-y-2">
                  {features.map((f, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="text-amber-400 mt-2.5 flex-shrink-0">✦</span>
                      <input value={f} onChange={e => setFeature(i, e.target.value)}
                        className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder={`특징 ${i + 1} (예: 100% 천연 소가죽)`} />
                      <button onClick={() => removeFeature(i)}
                        className="text-stone-300 hover:text-red-400 text-xl leading-none mt-1">×</button>
                    </div>
                  ))}
                  <button onClick={addFeature}
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 mt-2">
                    <span className="text-lg">+</span> 특징 추가
                  </button>
                </div>
              </div>

              {/* 스펙 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-amber-500 rounded-full inline-block"/>
                  제품 스펙
                </h2>
                <p className="text-xs text-stone-400 mb-3">항목 이름과 값을 입력하세요</p>
                <div className="space-y-2">
                  {specs.map((s, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2">
                      <input value={s.key} onChange={e => setSpec(i, 'key', e.target.value)}
                        className="col-span-2 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="항목 (예: 소재)" />
                      <input value={s.value} onChange={e => setSpec(i, 'value', e.target.value)}
                        className="col-span-2 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                        placeholder="값 (예: 천연 소가죽)" />
                      <button onClick={() => removeSpec(i)}
                        className="text-stone-300 hover:text-red-400 text-xl">×</button>
                    </div>
                  ))}
                  <button onClick={addSpec}
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 mt-2">
                    <span className="text-lg">+</span> 항목 추가
                  </button>
                </div>

                {/* 스펙 미리보기 */}
                {specs.some(s => s.key.trim()) && (
                  <div className="mt-4 bg-stone-50 rounded-xl overflow-hidden">
                    <p className="text-xs text-stone-400 px-4 py-2 border-b border-stone-100">스펙 미리보기</p>
                    <table className="w-full text-sm">
                      <tbody>
                        {specs.filter(s => s.key.trim()).map((s, i) => (
                          <tr key={i} className="border-b border-stone-100 last:border-0">
                            <td className="px-4 py-2.5 font-semibold text-stone-500 w-1/3">{s.key}</td>
                            <td className="px-4 py-2.5 text-stone-700">{s.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── 상세 설명 탭 ── */}
          {tab === 'detail' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="font-bold text-stone-800 mb-2 flex items-center gap-2">
                <span className="w-1 h-5 bg-amber-500 rounded-full inline-block"/>
                상세 설명 (HTML)
              </h2>
              <p className="text-xs text-stone-400 mb-4">
                HTML 태그를 사용할 수 있습니다. &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;img&gt;, &lt;strong&gt; 등
              </p>

              {/* 빠른 삽입 버튼 */}
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { label: '제목', insert: '<h3 style="font-size:18px;font-weight:bold;margin:16px 0 8px;">제목</h3>' },
                  { label: '단락', insert: '<p style="margin:8px 0;">내용을 입력하세요.</p>' },
                  { label: '목록', insert: '<ul style="list-style:disc;padding-left:20px;margin:8px 0;">\n  <li>항목 1</li>\n  <li>항목 2</li>\n</ul>' },
                  { label: '구분선', insert: '<hr style="margin:20px 0;border-color:#e5e7eb;" />' },
                  { label: '이미지', insert: '<img src="/uploads/products/파일명.jpg" style="width:100%;border-radius:12px;margin:12px 0;" alt="" />' },
                ].map(btn => (
                  <button key={btn.label} type="button"
                    onClick={() => setDetailContent(prev => prev + '\n' + btn.insert)}
                    className="text-xs bg-stone-100 hover:bg-amber-100 text-stone-600 hover:text-amber-700 px-3 py-1.5 rounded-lg transition font-medium">
                    {btn.label} 추가
                  </button>
                ))}
              </div>

              <textarea
                value={detailContent}
                onChange={e => setDetailContent(e.target.value)}
                rows={20}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400 resize-y"
                placeholder={'<h3 style="font-weight:bold;">제품 소개</h3>\n<p>여기에 상세 내용을 입력하세요.</p>'}
              />
              <p className="text-xs text-stone-400 mt-2">
                {detailContent.length}자 입력됨
              </p>
            </div>
          )}

          {/* ── 갤러리 탭 ── */}
          {tab === 'gallery' && (
            <div className="space-y-6">
              {/* 업로드 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-amber-500 rounded-full inline-block"/>
                  갤러리 이미지
                </h2>
                <input ref={galleryRef} type="file" multiple accept="image/*"
                  onChange={handleGalleryUpload} className="hidden" />
                <button onClick={() => galleryRef.current?.click()} disabled={uploading}
                  className="w-full border-2 border-dashed border-stone-300 hover:border-amber-400 rounded-2xl py-8 flex flex-col items-center gap-2 text-stone-400 hover:text-amber-500 transition disabled:opacity-50">
                  <span className="text-4xl">📁</span>
                  <span className="text-sm font-semibold">{uploading ? '업로드 중...' : '이미지 선택 (여러 장 가능)'}</span>
                  <span className="text-xs">JPG, PNG, WEBP · 5MB 이하</span>
                </button>
              </div>

              {/* 갤러리 목록 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-stone-700 mb-4">
                  갤러리 ({(product.gallery ?? []).length}장)
                </h3>
                {(product.gallery ?? []).length === 0 ? (
                  <p className="text-center text-stone-400 text-sm py-8">갤러리 이미지가 없습니다.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {(product.gallery ?? []).map((url, i) => (
                      <div key={i} className="relative group rounded-xl overflow-hidden aspect-square bg-stone-100">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                          <button onClick={() => deleteGalleryImage(url)}
                            className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 대표 이미지 */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-stone-700 mb-4">대표 이미지</h3>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 bg-stone-100 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
                    {product.image_url
                      ? <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                      : <span className="text-3xl">이미지 없음</span>}
                  </div>
                  <div className="text-sm text-stone-500">
                    대표 이미지는 <Link to="/admin/catalog" className="text-amber-600 hover:underline">제품 카탈로그 관리</Link>에서 변경할 수 있습니다.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── 미리보기 탭 ── */}
          {tab === 'preview' && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-stone-800 text-white px-4 py-2.5 text-xs flex items-center gap-2">
                <span className="text-stone-400">미리보기</span>
                <span className="text-stone-500">—</span>
                <a href={`/product/${id}`} target="_blank" rel="noreferrer"
                  className="text-amber-400 hover:underline">실제 페이지에서 보기 ↗</a>
              </div>
              <div className="p-6 space-y-6">

                {/* 제품 기본 */}
                <div className="flex gap-6">
                  <div className="w-40 h-40 bg-stone-100 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
                    {product.image_url
                      ? <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                      : <span className="text-4xl">이미지 없음</span>}
                  </div>
                  <div>
                    <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">{product.category}</span>
                    <h3 className="text-xl font-bold text-stone-900 mt-2">{product.name}</h3>
                    <p className="text-amber-600 font-bold text-lg mt-1">{product.price.toLocaleString()}원</p>
                    {description && (
                      <p className="text-sm text-stone-600 mt-2 border-l-4 border-amber-400 pl-3">{description}</p>
                    )}
                  </div>
                </div>

                {/* 특징 */}
                {features.some(f => f.trim()) && (
                  <div>
                    <h4 className="font-bold text-stone-700 mb-2 text-sm">제품 특징</h4>
                    <ul className="space-y-1">
                      {features.filter(f => f.trim()).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                          <span className="text-amber-500">✦</span>{f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 스펙 */}
                {specs.some(s => s.key.trim()) && (
                  <div className="bg-stone-50 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {specs.filter(s => s.key.trim()).map((s, i) => (
                          <tr key={i} className="border-b border-stone-100 last:border-0">
                            <td className="px-4 py-2.5 font-semibold text-stone-500 w-1/3">{s.key}</td>
                            <td className="px-4 py-2.5 text-stone-700">{s.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 상세 HTML */}
                {detailContent && (
                  <div className="border-t border-stone-100 pt-6">
                    <h4 className="font-bold text-stone-700 mb-3 text-sm">상세 설명</h4>
                    <div className="text-sm text-stone-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: detailContent }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
